/**
 * Security Settings API Client
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 */

import { fetchWithAuth } from '@/utils/api';

export interface TenantSecuritySettings {
  id: string;
  tenantId: string;
  // Session timeout
  sessionIdleTimeoutMins: number;
  sessionMaxLifetimeHours: number;
  rememberMeTimeoutDays: number;
  // Session controls
  maxConcurrentSessions: number;
  forceLogoutOnIpChange: boolean;
  requireReauthForSensitive: boolean;
  // Password policy
  passwordHistoryCount: number;
  passwordMaxAgeDays: number;
  passwordMinLength: number;
  passwordRequireComplex: boolean;
  // Account lockout
  accountLockoutAttempts: number;
  accountLockoutDuration: number;
  // Inactivity policy
  inactivityWarningDays: number;
  inactivityRevocationDays: number;
  // 2FA
  require2FA: boolean;
  require2FAForAdmins: boolean;
  // Audit
  createdAt: string;
  updatedAt: string;
  lastModifiedBy?: string;
}

export interface SecuritySettingsUpdate {
  sessionIdleTimeoutMins?: number;
  sessionMaxLifetimeHours?: number;
  rememberMeTimeoutDays?: number;
  maxConcurrentSessions?: number;
  forceLogoutOnIpChange?: boolean;
  requireReauthForSensitive?: boolean;
  passwordHistoryCount?: number;
  passwordMaxAgeDays?: number;
  passwordMinLength?: number;
  passwordRequireComplex?: boolean;
  accountLockoutAttempts?: number;
  accountLockoutDuration?: number;
  inactivityWarningDays?: number;
  inactivityRevocationDays?: number;
  require2FA?: boolean;
  require2FAForAdmins?: boolean;
}

export interface SecuritySettingsSummary {
  sessionTimeout: string;
  maxSessions: string;
  passwordPolicy: string;
  lockoutPolicy: string;
  inactivityPolicy: string;
  twoFactorPolicy: string;
}

export interface BreachCheckStatus {
  emailChecksEnabled: boolean;
  passwordChecksEnabled: boolean;
  exposureThreshold: number;
  provider: string;
  documentation: string;
}

export interface PasswordBreachResult {
  compromised: boolean;
  exposureCount: number;
  message?: string;
  recommendation?: string;
}

export interface EmailBreachResult {
  breached: boolean;
  breachCount: number;
  breaches: Array<{
    name: string;
    title?: string;
    domain?: string;
    breachDate: string;
    dataClasses?: string[];
    pwnCount?: number;
  }>;
  checkedAt: string;
  recommendation?: string;
}

const API_BASE = '/api/v1';

export const securitySettingsApi = {
  /**
   * Get current security settings
   */
  async getSettings(): Promise<TenantSecuritySettings> {
    const response = await fetchWithAuth(`${API_BASE}/security-settings`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Update security settings
   */
  async updateSettings(updates: SecuritySettingsUpdate): Promise<TenantSecuritySettings> {
    const response = await fetchWithAuth(`${API_BASE}/security-settings`, {
      method: 'PUT',

      body: JSON.stringify(updates),
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Get security settings summary
   */
  async getSummary(): Promise<SecuritySettingsSummary> {
    const response = await fetchWithAuth(`${API_BASE}/security-settings/summary`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Reset to default settings
   */
  async resetToDefaults(): Promise<TenantSecuritySettings> {
    const response = await fetchWithAuth(`${API_BASE}/security-settings/reset`, {
      method: 'POST',
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Get default settings (for reference)
   */
  async getDefaults(): Promise<Partial<TenantSecuritySettings>> {
    const response = await fetchWithAuth(`${API_BASE}/security-settings/defaults`);
    const data = await response.json();
    return data.data;
  },
};

export const breachDetectionApi = {
  /**
   * Get breach detection service status
   */
  async getStatus(): Promise<BreachCheckStatus> {
    const response = await fetchWithAuth(`${API_BASE}/security/breach-check/status`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Check if password is compromised
   */
  async checkPassword(password: string): Promise<PasswordBreachResult> {
    const response = await fetchWithAuth(`${API_BASE}/security/breach-check/password`, {
      method: 'POST',

      body: JSON.stringify({ password }),
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Check email breaches
   */
  async checkEmail(): Promise<EmailBreachResult> {
    const response = await fetchWithAuth(`${API_BASE}/security/breach-check/email`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Get all known breaches
   */
  async getAllBreaches(): Promise<Array<{
    name: string;
    title?: string;
    domain?: string;
    breachDate: string;
    pwnCount?: number;
    dataClasses?: string[];
    isVerified?: boolean;
  }>> {
    const response = await fetchWithAuth(`${API_BASE}/security/breach-check/breaches`);
    const data = await response.json();
    return data.data.breaches;
  },
};
